/**
 * Phase 4.13.2 — visual alignment with the product design system.
 * Phase 4.5.1 — collapsible-by-default to save vertical space.
 *
 * The card renders the same data as before but in the parchment+
 * ink idiom used by JobCard and DetailScreen — uppercase section
 * labels in --ink-3, JetBrains Mono everywhere, --good marker
 * stripe on the left, soft 1px outline, no accent-colour fills, no
 * emojis. ``BatchSummaryFeedCard.css`` carries the matching
 * tokens.
 *
 * Phase 4.5.1: the card opens collapsed by default. The header,
 * compact meta, progress bar and counts line are always visible —
 * those are the "at-a-glance" facts. The station decomposition,
 * substitution chain and "Все задачи →" action live inside the
 * collapsible region. Operators who want detail click the header
 * to expand; the rest stays compact in the feed.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { formatDateTime, formatDuration } from '../lib/format';
import { formatGnssList } from '../lib/gnss_labels';
import type { BatchSummary, FailedCategory } from '../api/types';
import './BatchSummaryFeedCard.css';

interface Props {
  summary: BatchSummary;
  /** Phase 6.7 A — when ``true`` AND no explicit localStorage entry
   *  exists for this tag yet, the card initialises expanded instead
   *  of collapsed. Used by FeedScreen to mark the freshest series
   *  (MAX ``first_created_at`` among visible items) so the operator
   *  always sees what they came for without an extra click. Defaults
   *  to ``false`` so call sites that don't care preserve the
   *  collapsed-by-default behaviour from Phase 4.5.1. */
  defaultExpanded?: boolean;
}

/** Phase 6.7 A — localStorage key for per-card expand persistence.
 *  Value is JSON-serialised ``{ "expanded": [tag, …] }``: an array
 *  of tag strings the operator has manually expanded. Bounded to
 *  20 entries via FIFO (oldest first) so a long-running operator
 *  doesn't grow the key unboundedly across hundreds of series. */
const EXPANDED_STORAGE_KEY = 'gnss-qa:series-cards-expanded';
const EXPANDED_FIFO_LIMIT = 20;

interface ExpandedState {
  expanded: string[];
}

function _readExpanded(): string[] {
  try {
    const raw = localStorage.getItem(EXPANDED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ExpandedState | null;
    if (!parsed || !Array.isArray(parsed.expanded)) return [];
    // Re-coerce to ensure the array carries only strings — defensive
    // against a hand-edit in DevTools or a future schema bump.
    return parsed.expanded.filter((t): t is string => typeof t === 'string');
  } catch {
    // Phase 6.1 §C — Telegram WebView in privacy mode rejects
    // localStorage access. Falling back to «no persisted expansion»
    // preserves the in-session toggle behaviour without crashing.
    return [];
  }
}

function _writeExpanded(tags: string[]): void {
  try {
    // FIFO cap — drop oldest entries first so the most-recently-
    // expanded series survive across visits.
    const trimmed = tags.slice(-EXPANDED_FIFO_LIMIT);
    localStorage.setItem(
      EXPANDED_STORAGE_KEY,
      JSON.stringify({ expanded: trimmed } satisfies ExpandedState),
    );
  } catch {
    // Same Telegram-privacy fallback — losing persistence is
    // preferable to throwing on every toggle.
  }
}

function _toggleStoredTag(tag: string, nowExpanded: boolean): void {
  const current = _readExpanded();
  // Remove duplicates first — if a tag is already in the list, we
  // either drop it (collapse) or move it to the tail (re-expanded).
  const without = current.filter((t) => t !== tag);
  if (nowExpanded) {
    _writeExpanded([...without, tag]);
  } else {
    _writeExpanded(without);
  }
}

/** Phase 6.3 → Phase R — operator-readable labels for the six v2
 *  FAILED categories, retitled under the Phase R terminology
 *  (designer-final wording per spec). The V2 enum keeps its
 *  shape on the backend (the BatchCard's per-row drill-down still
 *  uses ``?failed_category=`` URL filters), but each row now
 *  reads with the «не применимо · файл недоступен · не вычислено»
 *  vocabulary the operator sees on the main screen. */
const FAILED_CATEGORY_LABEL: Record<FailedCategory, string> = {
  rinex_real_missing: 'не применимо · станция не пишет систему',
  rinex_parse_error: 'прочее · RINEX не разобрался',
  rinex_not_available: 'файл недоступен · не получен',
  compute_failed_ambiguity: 'не вычислено · ambiguity не сошёлся',
  service_compute_failed: 'не вычислено · сервис вернул сбой',
  other: 'прочее',
};

const FAILED_CATEGORY_ALARM_LABEL: Record<FailedCategory, string> = {
  rinex_real_missing: 'ожидаемо',
  rinex_parse_error: 'требует расследования',
  rinex_not_available: 'ожидаемо',
  compute_failed_ambiguity: 'сбой сервиса',
  service_compute_failed: 'сбой сервиса',
  other: 'требует анализа',
};

/** Phase 6.3 — CSS modifier suffix routed to
 *  `.bsfc-failed-row__alarm--{key}` so each label gets its own
 *  colour without inline styles. Three levels:
 *
 *  - `ok` (muted ink) — expected catalog-limit categories.
 *  - `warn` (amber) — service-side failures, operator may want to
 *    look but it's not a smoking gun.
 *  - `error` (red) — `rinex_parse_error` (potential parser bug) and
 *    `other` (operational issues like login failures). The shared
 *    treatment is intentional: both warrant immediate operator
 *    attention even if the root causes differ. */
const FAILED_CATEGORY_ALARM_KEY: Record<FailedCategory, string> = {
  rinex_real_missing: 'ok',
  rinex_parse_error: 'error',
  rinex_not_available: 'ok',
  compute_failed_ambiguity: 'warn',
  service_compute_failed: 'warn',
  other: 'error',
};

/** Phase 6.3 — display order for the «ОШИБКИ» block. Expected
 *  categories (low alarm) first, then service-side, then high-alarm
 *  buckets at the bottom — reading order matches the operator's
 *  triage order ("expected stuff first, alarming stuff last"). */
const FAILED_CATEGORY_ORDER: FailedCategory[] = [
  'rinex_real_missing',
  'rinex_not_available',
  'compute_failed_ambiguity',
  'service_compute_failed',
  'rinex_parse_error',
  'other',
];

/** Phase 6.5 B — outlier threshold per spec §B.4 — anything worse
 *  than 100 mm is rendered with the error palette so the operator
 *  spots a bad row at a glance. */
const OUTLIER_DR_THRESHOLD_MM = 100;

function _formatDr(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  // Match the heatmap-cell format (one decimal) — keeps the
  // ТОЧНОСТЬ block visually consistent with the analytics screen.
  return `${value.toFixed(1)} мм`;
}

export function BatchSummaryFeedCard({
  summary,
  defaultExpanded = false,
}: Props) {
  // Phase 6.7 A — collapsed-by-default with two override layers:
  //
  // 1. ``defaultExpanded`` prop — set by FeedScreen on the freshest
  //    series (MAX ``first_created_at`` among visible cards) so the
  //    operator always sees what they came for without an extra
  //    click. Applies on first render only when the localStorage
  //    list doesn't already contain this tag.
  //
  // 2. localStorage list under ``EXPANDED_STORAGE_KEY`` — every
  //    manual expand/collapse persists, FIFO 20, so refreshing the
  //    page restores the expand state of the operator's recent
  //    interactions. Phase 4.5.1's "no persistence" rationale
  //    (cards re-collapse on every view) was wrong for «I'm
  //    investigating one series» — the persistent variant matches
  //    how operators actually use the feed.
  //
  // The lazy initialiser pattern (function form of useState) reads
  // localStorage once at mount and never again; subsequent toggles
  // round-trip through ``_toggleStoredTag``.
  const [expanded, setExpanded] = useState<boolean>(() => {
    const stored = _readExpanded();
    if (stored.includes(summary.tag)) return true;
    return defaultExpanded;
  });

  const handleToggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      _toggleStoredTag(summary.tag, next);
      return next;
    });
  };

  const isCompleted =
    summary.last_job_finished_at !== null &&
    summary.last_job_finished_at !== undefined;

  const successRate =
    summary.total_jobs > 0
      ? Math.round((100 * (summary.counts.done ?? 0)) / summary.total_jobs)
      : 0;

  const duration = isCompleted
    ? formatDuration(summary.first_created_at, summary.last_job_finished_at)
    : null;

  const requested = summary.requested_station_count ?? 0;
  const substituted = summary.substituted_station_count ?? 0;
  const totalUnique = summary.total_unique_stations ?? 0;
  const uniqueDone = summary.unique_stations_done ?? 0;
  const pairs = summary.substitution_pairs ?? [];

  // Exact-match link uses the Phase-4.11 quote syntax so a tag
  // containing one batch's prefix doesn't accidentally surface
  // unrelated batches' jobs.
  const filterHref = `/?q=${encodeURIComponent(`"${summary.tag}"`)}`;

  return (
    <article
      className={`batch-summary-feed-card ${isCompleted ? 'completed' : 'active'} ${
        expanded ? 'expanded' : 'collapsed'
      }`}
      data-testid="batch-summary-feed-card"
      data-tag={summary.tag}
      data-expanded={expanded}
    >
      <header
        className="bsfc-header"
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls={`bsfc-body-${summary.tag}`}
        data-testid="bsfc-header"
      >
        <span className="bsfc-section-label">Серия</span>
        <span className="bsfc-tag" title={summary.tag}>
          {summary.tag}
        </span>
        <span
          className={`bsfc-status ${isCompleted ? 'completed' : 'active-state'}`}
        >
          {isCompleted ? 'Завершена' : 'В работе'}
        </span>
        <span className="bsfc-toggle" aria-hidden="true">
          {expanded ? '▲' : '▼'}
        </span>
      </header>

      <div className="bsfc-meta">
        <span>{formatDateTime(summary.first_created_at)}</span>
        {duration && (
          <>
            <span className="bsfc-meta-sep">·</span>
            <span>{duration}</span>
          </>
        )}
      </div>

      {/* Wave-5.1 — interval row for convergence (slice) series. */}
      {summary.lengths_h && summary.lengths_h.length > 0 && (
        <div className="bsfc-intervals">
          интервалы:{' '}
          {summary.lengths_h
            .map((l) => (l % 1 === 0 ? `${l}` : l.toFixed(1)))
            .join(' ')}{' '}
          ч
        </div>
      )}

      <div className="bsfc-counts">
        <strong>{summary.total_jobs}</strong> задач
        <span className="label-sep">·</span>
        выполнено <strong>{summary.counts.done ?? 0}</strong>
        <span className="label-sep">·</span>
        ошибок <strong>{summary.counts.failed ?? 0}</strong>
        {/* Phase 6.5 E — «отменено N» suffix, only rendered when
            there's at least one cancelled job. Hidden entirely for
            the common case so legacy series look identical. */}
        {(summary.counts.cancelled ?? 0) > 0 && (
          <>
            <span className="label-sep">·</span>
            отменено <strong>{summary.counts.cancelled}</strong>
          </>
        )}
      </div>

      {/* Phase 6.6 A — completes the Phase-6.7 collapsible split.
          Phase 6.7 gated only `bsfc-body` (stations / substitutions /
          «Все задачи →» footer) behind `expanded`, but left the
          progress bar, FailedBreakdownBlock and AccuracyBlock
          rendering unconditionally. That left the collapsed card
          showing 6 visual rows instead of the spec's three. Moving
          all three blocks under the `{expanded && …}` guard here
          finalises the "exactly 3 lines when collapsed" contract:
          header + meta + counts. */}
      {expanded && (
        <div id={`bsfc-body-${summary.tag}`} className="bsfc-body">
          <div className="bsfc-progress" aria-hidden="true">
            <div
              className="bsfc-progress-bar"
              style={{ width: `${successRate}%` }}
            />
          </div>

          {/* Phase 6.5 A — collapsible FAILED-breakdown block.
              Hidden when there are no failures or every bucket is
              zero (defensive — backend always fills three keys but
              they can all be 0). Per spec §A.3 collapsed by default
              AT ALL TIMES so the card stays compact; expand on click.

              Buckets with count==0 are NOT rendered (the spec's
              «Категория с нулевым счётчиком — не отображается» rule). */}
          {(summary.counts.failed ?? 0) > 0 && summary.failed_breakdown && (
            <FailedBreakdownBlock
              breakdown={summary.failed_breakdown}
              tag={summary.tag}
            />
          )}

          {/* Phase 6.5 B — ТОЧНОСТЬ block. Visible when the series
              has at least one DONE-with-residual job. Backend
              collapses to ``null`` otherwise so this branch hides
              automatically. Phase 6.6 A moved this inside the
              expanded-only region — see above. */}
          {summary.accuracy && (
            <AccuracyBlock summary={summary} />
          )}

          {/* Phase 6.7 A — removed Phase-6.5 «ПО КОНФИГУРАЦИЯМ» block.
              It duplicated the analytics-screen heatmap (per-config
              accuracy is now seen across the entire DB, not one series),
              ate ~150 px of vertical real estate per card, and split
              operator attention from the «ПО СТАНЦИЯМ» block that is
              actually unique to one series. ``summary.per_config`` is
              still on the wire (backend Phase 6.5 keeps emitting it)
              so the API contract stays stable — we just stop rendering. */}
          <div className="bsfc-section">
            <div className="bsfc-section-title">По станциям</div>
            <div className="bsfc-station-detail">
              {requested} запрошено
              {substituted > 0 && (
                <>
                  {' '}
                  + {substituted} автозамены = {totalUnique} попробовано
                </>
              )}
            </div>
            <div className="bsfc-station-detail">
              {uniqueDone} дали хотя бы один успешный результат
            </div>
          </div>

          {pairs.length > 0 && (
            <details className="bsfc-substitutions">
              <summary className="bsfc-substitutions-summary">
                Замены ({pairs.length})
              </summary>
              <ul className="bsfc-substitution-list">
                {pairs.map((pair) => (
                  <li key={`${pair.original_code}-${pair.new_code}`}>
                    <span className="bsfc-orig">{pair.original_code}</span>
                    <span className="bsfc-arrow" aria-hidden="true">
                      →
                    </span>
                    <span className="bsfc-new">{pair.new_code}</span>
                    {pair.config_count > 1 && (
                      <span className="bsfc-config-count">
                        ×{pair.config_count}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <footer className="bsfc-footer">
            <Link
              to={filterHref}
              className="bsfc-action"
              data-testid="bsfc-action-link"
            >
              Все задачи →
            </Link>
          </footer>
        </div>
      )}
    </article>
  );
}


// ---------------------------------------------------------------- Phase 6.5 A


/** «ОШИБКИ (47) ▼» raise-on-click block. Collapsed by default per
 *  spec §A.3. Renders one row per non-zero category, in the canonical
 *  display order, with a click-to-drill-down hyperlink that lands on
 *  the Feed pre-filtered to (tag × failed_category). */
function FailedBreakdownBlock({
  breakdown,
  tag,
}: {
  breakdown: Record<string, number>;
  tag: string;
}) {
  const total = FAILED_CATEGORY_ORDER.reduce(
    (sum, cat) => sum + (breakdown[cat] ?? 0),
    0,
  );
  // Defensive: the breakdown can be all-zero on a malformed payload.
  // The parent guard already checks counts.failed > 0 but we
  // re-check here so this component is safe to drop into any
  // context.
  if (total === 0) return null;

  return (
    <details className="bsfc-failed-breakdown">
      <summary className="bsfc-failed-summary">
        ОШИБКИ ({total})
      </summary>
      <ul className="bsfc-failed-list">
        {FAILED_CATEGORY_ORDER.map((cat) => {
          const count = breakdown[cat] ?? 0;
          if (count === 0) return null;
          const alarmKey = FAILED_CATEGORY_ALARM_KEY[cat];
          const href = `/?tag=${encodeURIComponent(`"${tag}"`)}&failed_category=${cat}`;
          return (
            <li
              key={cat}
              className="bsfc-failed-row"
              data-testid={`bsfc-failed-row-${cat}`}
            >
              <Link className="bsfc-failed-link" to={href}>
                <span className="bsfc-failed-count">{count}</span>
                <span className="bsfc-failed-label">
                  {FAILED_CATEGORY_LABEL[cat]}
                </span>
                <span
                  className={`bsfc-failed-alarm bsfc-failed-alarm--${alarmKey}`}
                >
                  [{FAILED_CATEGORY_ALARM_LABEL[cat]}]
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </details>
  );
}


// ---------------------------------------------------------------- Phase 6.5 B


/** ТОЧНОСТЬ block — single visible aggregate per spec §B.4. The
 *  worst row gets the `--error` modifier when its dr_3d_mm exceeds
 *  the outlier threshold, so the operator spots km-scale anomalies
 *  at a glance even before opening the DetailScreen. */
function AccuracyBlock({ summary }: { summary: BatchSummary }) {
  const accuracy = summary.accuracy;
  if (!accuracy) return null;  // type narrowing — already guarded by caller

  const tag = summary.tag;
  const without = accuracy.without_residual;
  const skipHref = `/?tag=${encodeURIComponent(`"${tag}"`)}&residual_skip=true`;
  const worstIsOutlier =
    accuracy.worst_dr_mm !== null &&
    accuracy.worst_dr_mm > OUTLIER_DR_THRESHOLD_MM;

  return (
    <section
      className="bsfc-accuracy"
      data-testid="bsfc-accuracy"
    >
      <div className="bsfc-section-title">ТОЧНОСТЬ</div>
      <div className="bsfc-accuracy-counts">
        <strong>{accuracy.with_residual}</strong> из{' '}
        <strong>{accuracy.total_done}</strong> с измеренной |dR|
        {without > 0 && (
          <>
            <span className="label-sep">·</span>
            <Link
              className="bsfc-accuracy-skip"
              to={skipHref}
              data-testid="bsfc-accuracy-skip-link"
            >
              {without} без эталона
            </Link>
          </>
        )}
      </div>
      {accuracy.fallback_residual > 0 && (
        <div className="bsfc-accuracy-counts" data-testid="bsfc-accuracy-split">
          точный эталон:{' '}
          <strong>{accuracy.with_residual - accuracy.fallback_residual}</strong>
          <span className="label-sep">·</span>
          fallback ±5 дней: <strong>{accuracy.fallback_residual}</strong>
        </div>
      )}
      <ul className="bsfc-accuracy-stats">
        <li className="bsfc-accuracy-row">
          <span className="bsfc-accuracy-label">медиана |dR|</span>
          <span className="bsfc-accuracy-value">
            {_formatDr(accuracy.median_dr_mm)}
          </span>
        </li>
        {accuracy.best_job_id !== null && (
          <li className="bsfc-accuracy-row">
            <Link
              className="bsfc-accuracy-link"
              to={`/job/${accuracy.best_job_id}`}
              data-testid="bsfc-accuracy-best-link"
            >
              <span className="bsfc-accuracy-label">лучший</span>
              <span className="bsfc-accuracy-target">
                {accuracy.best_station ?? '?'}
                {accuracy.best_config && (
                  <>
                    {' × '}
                    {formatGnssList(accuracy.best_config.split(','))}
                  </>
                )}
              </span>
              <span className="bsfc-accuracy-value">
                {_formatDr(accuracy.best_dr_mm)}
              </span>
              <span className="bsfc-accuracy-arrow" aria-hidden="true">
                →
              </span>
            </Link>
          </li>
        )}
        {accuracy.worst_job_id !== null && (
          <li
            className={`bsfc-accuracy-row ${
              worstIsOutlier ? 'bsfc-accuracy-row--outlier' : ''
            }`}
          >
            <Link
              className="bsfc-accuracy-link"
              to={`/job/${accuracy.worst_job_id}`}
              data-testid="bsfc-accuracy-worst-link"
            >
              <span className="bsfc-accuracy-label">худший</span>
              <span className="bsfc-accuracy-target">
                {accuracy.worst_station ?? '?'}
                {accuracy.worst_config && (
                  <>
                    {' × '}
                    {formatGnssList(accuracy.worst_config.split(','))}
                  </>
                )}
              </span>
              <span className="bsfc-accuracy-value">
                {_formatDr(accuracy.worst_dr_mm)}
              </span>
              <span className="bsfc-accuracy-arrow" aria-hidden="true">
                →
              </span>
            </Link>
          </li>
        )}
      </ul>
    </section>
  );
}


// Phase 6.7 A — removed PerConfigBlock component. The per-config
// breakdown that lived here in Phase 6.5 duplicated the analytics
// screen's heatmap (which now shows the same data across the WHOLE
// DB instead of one series), so we cut the JSX path entirely.
// ``BatchSummary.per_config`` is still on the wire; renderers
// outside the FeedCard can read it without going through us.
